import React from "react";
import { Intercom, LiveChatLoaderProvider } from "react-live-chat-loader";
import { isTrialOnly, getBootstrappedCC } from "@utils/geo";
import { getTargetTrialUrl, TARGET_TRIAL_URL } from "@constants/urls";
import dealfrontLogoWhite from "@icons/dealfrontLogoWhite";
import { GeoProvider } from "@context/GeoContext";
import GlobalContextProvider from "@context/context";

// Runtime GTM loader
export function loadGTM({ id = "GTM-WNTS5V4" } = {}) {
  try {
    if (typeof window === "undefined") return;
    if (window.__GTM_LOADED__) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
    s.crossOrigin = "anonymous";
    document.head.appendChild(s);
    window.__GTM_LOADED__ = true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("loadGTM failed", e);
  }
}

if (typeof window !== "undefined") {
  window.loadGTM = window.loadGTM || loadGTM;
}

function rewriteDemoLinks() {
  const cc = getBootstrappedCC();
  if (!isTrialOnly(cc)) return;

  const anchors = document.querySelectorAll(
    "a[href*='request-demo'], a[href*='demo']"
  );
  anchors.forEach((a) => {
    if (a.href.startsWith(TARGET_TRIAL_URL)) return;
    const email = (document.cookie.match(/(?:^|;)\\s*emailForm=([^;]*)/) ||
      [])[1];
    a.href = getTargetTrialUrl(email);
    a.id = "start-free-trial-cta";
    a.textContent = "Try for free";
    a.addEventListener(
      "click",
      () => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "clicked_on_free_trial_cta",
          cta_text: "Try for free",
          cta_href: a.href,
          page_path: window.location.pathname,
          source: "nav_cta",
        });
      },
      { once: true }
    );
  });
}

// Client-side geo bootstrap (matches SSR inline behavior).
function bootstrapGeo() {
  if (typeof window === "undefined") return;
  try {
    const qs = window.location.search || "";
    const getQP = (name) => {
      try {
        const m = qs.match(new RegExp("[?&]" + name + "=([^&]+)"));
        return m ? decodeURIComponent(m[1]) : "";
      } catch (e) {
        return "";
      }
    };

    const getCookie = (n) => {
      try {
        return (
          ("; " + document.cookie)
            .split("; " + n + "=")
            .pop()
            .split(";")[0] || ""
        );
      } catch (e) {
        return "";
      }
    };

    const setCCCookie = (v) => {
      try {
        const parts = [
          "df_cc=" + v,
          "Max-Age=3600",
          "Path=/",
          "SameSite=Lax",
          "Secure",
        ];
        if (/\.?dealfront\.com$/i.test(window.location.hostname)) {
          parts.push("Domain=.dealfront.com");
        }
        document.cookie = parts.join("; ");
      } catch (e) {
        /* ignore */
      }
    };

    // QA override
    const qaCC = getQP("qa_cc");
    if (qaCC) {
      const v = String(qaCC).toUpperCase();
      window.__DF_CC = v;
      setCCCookie(v);
      document.dispatchEvent(new CustomEvent("df:cc", { detail: { cc: v } }));
      return;
    }

    // Cookie bootstrap
    const cc = (getCookie("df_cc") || "").toUpperCase();
    window.__DF_CC = cc || "";
    if (cc) return;

    const onDealfront = /\.?dealfront\.com$/i.test(window.location.hostname);
    const GEO_URL = onDealfront
      ? window.location.origin + "/_api/geo"
      : "https://www.dealfront.com/_api/geo";

    const doFetch = () => {
      const url =
        GEO_URL + (GEO_URL.indexOf("?") > -1 ? "&" : "?") + "t=" + Date.now();
      try {
        fetch(url, { credentials: "omit", cache: "no-store", mode: "cors" })
          .then((r) => {
            if (!r.ok) throw new Error("HTTP " + r.status);
            return r.json();
          })
          .then((o) => {
            const v = String((o && o.cc) || "").toUpperCase();
            if (!v) throw new Error("no cc in response");
            window.__DF_CC = v;
            setCCCookie(v);
            document.dispatchEvent(
              new CustomEvent("df:cc", { detail: { cc: v } })
            );
          })
          .catch(() => {
            /* ignore fetch failure */
          });
      } catch (e) {
        /* ignore */
      }
    };

    // Perform the fetch immediately to preserve the original
    // inline-SSR behavior (the SSR script executed the fetch
    // synchronously when no cookie/QA override was present).
    try {
      doFetch();
    } catch (e) {
      // ignore
    }
  } catch (e) {
    // ignore
  }
}

export const onClientEntry = () => {
  // Ensure a dataLayer array exists early so components can safely push events
  if (typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || [];
  }

  const loadVWO = () => {
    try {
      if (document.getElementById("vwoCode")) return;
      const s = document.createElement("script");
      s.type = "text/javascript";
      s.id = "vwoCode";
      s.src = "https://dev.visualwebsiteoptimizer.com/j.php?a=765212";
      s.defer = true;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    } catch (e) {
      // fail silently
    }
  };

  try {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(
        () => {
          loadGTM();
          loadVWO();
        },
        { timeout: 3000 }
      );
    } else {
      setTimeout(() => {
        loadGTM();
        loadVWO();
      }, 2000);
    }
  } catch (e) {
    // ignore
  }
};

export const onInitialClientRender = () => {
  // run once on the very first load
  setTimeout(rewriteDemoLinks, 0);

  // run again when geo arrives
  document.addEventListener("df:cc", rewriteDemoLinks, { once: true });

  // Add class to enable plug animation after hydration
  // This prevents the animation from running twice (SSR + hydration)
  // const enablePlugAnimation = () => {
  //   if (document.documentElement) {
  //     document.documentElement.classList.add("js-loaded");
  //   }
  // };

  // Enable immediately if DOM is ready, otherwise wait for DOMContentLoaded
  // if (document.readyState === "loading") {
  //   document.addEventListener("DOMContentLoaded", enablePlugAnimation, {
  //     once: true,
  //   });
  // } else {
  //   enablePlugAnimation();
  // }

  // Run `bootstrapGeo` immediately only when a QA override (`qa_cc`)
  // is present in the querystring or when a `df_cc` cookie already
  // exists. Otherwise defer to `requestIdleCallback` (fallback to
  // `setTimeout`) to avoid work on the LCP-critical path.
  try {
    if (typeof window !== "undefined") {
      const qs = window.location.search || "";
      const hasQa = /[?&]qa_cc=/.test(qs);
      const hasDfCc = (function () {
        try {
          return (
            ("; " + document.cookie).split("; df_cc=").pop().split(";")[0] || ""
          );
        } catch (e) {
          return "";
        }
      })();

      if (hasQa || (hasDfCc && hasDfCc !== "")) {
        bootstrapGeo();
      } else if ("requestIdleCallback" in window) {
        requestIdleCallback(() => bootstrapGeo(), {
          timeout: 2500,
        });
      } else {
        setTimeout(() => bootstrapGeo(), 2000);
      }
    }
  } catch (e) {
    // ignore
  }
};

export const onRouteUpdate = () => {
  setTimeout(rewriteDemoLinks, 50);

  try {
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push({ event: "dealfront-page-change" });
    }
  } catch (e) {
    // ignore
  }
};

export const wrapRootElement = ({ element }) => {
  return (
    <LiveChatLoaderProvider provider="intercom" providerKey="bidodql1">
      <GeoProvider>
        <GlobalContextProvider>{element}</GlobalContextProvider>
      </GeoProvider>
      <Intercom color="#073572" icon={dealfrontLogoWhite} />
    </LiveChatLoaderProvider>
  );
};

export const shouldUpdateScroll = ({ routerProps: { location } }) => {
  if (location.hash === "") {
    setTimeout(() => window.scrollTo(0, 0), 10);
    return false;
  }
};
